import { Navigate } from 'react-router-dom';
import { lazy } from 'react';

import { Dashboard } from '@/pages';
import { ErrorPage } from '@/pages/Layout/pages';

import { CustomerServiceRoutes } from '@/pages/CustomerService/pages/Cases/routes';

import Sila from '@/pages/SILA/Sila';

import { AttendTrackRoutes } from '@/pages/AttendTrack/routes';
import {
  ROLES,
  BUSINESS_ROLE_GROUPS,
  COMMERCE_ROLE_NAMES,
  EXTERNAL_OPERATOR_ROLE_NAMES,
  userHasPermission,
} from '@/components/security/AppRoles';
import BusinessExternalOperatorsScopeGuard from '@/pages/BusinessExternalOperators/guards/BusinessExternalOperatorsScopeGuard';
import { BusinessExternalOperatorsRoutes } from '@/pages/BusinessExternalOperators/routes/BusinessExternalOperatorsRoutes';
import { BusinessCommercesRoutes } from '@/pages/BusinessCommerces/routes/BusinessCommercesRoutes';
import BusinessScopeGuard from '@/pages/Business/components/BusinessScopeGuard';
import AppsVersionsRoutes from '@/pages/AppsVersions/pages/AppsVersions';
import { ADMINISTRATION_PATH } from '@/pages/Administration/config';
import { InstitutionalWebRoutes } from '@/pages/InstitutionalWeb/routes/InstitutionalWebRoutes';
import { FleetRoutes } from '@/pages/Fleet/routes/FleetRoutes';
import { InventoryRoutes } from '@/pages/Inventory/routes';
import { BusinessRoutes } from '@/pages/Business/routes';
import {
  TECH_CHECKS_PATH,
  TechChecksRoutes,
} from '@/pages/TechChecks/routes/TechChecksRoutes';
import { TasksRoutes } from '@/pages/Scheduler/routes';
import FleetLayout from '@/pages/Fleet/FleetLayout';
import { RELEASE_NOTES_PATH } from '@/pages/ReleaseNotes/config/paths';

const Administration = lazy(
  () => import('@/pages/Administration/Administration')
);
const ReleaseNotes = lazy(() => import('@/pages/ReleaseNotes/ReleaseNotes'));

export interface Props {
  permissions: string[];
}

const PrivateRoutesMiddleware = ({ permissions }: Props) => {
  const routes = [];

  // Un usuario de comercio no tiene nada que ver en el Dashboard ni en el back
  // office: se lo manda directo a su sección.
  const isCommerceOnlyUser =
    userHasPermission(permissions, COMMERCE_ROLE_NAMES as ROLES[]) &&
    !userHasPermission(permissions, BUSINESS_ROLE_GROUPS.full);

  // Ídem para un operador externo: tampoco tiene nada que ver en el
  // Dashboard ni en el back office.
  const isExternalOperatorOnlyUser =
    userHasPermission(permissions, EXTERNAL_OPERATOR_ROLE_NAMES as ROLES[]) &&
    !userHasPermission(permissions, BUSINESS_ROLE_GROUPS.full);

  const homeRedirect = isCommerceOnlyUser
    ? '/business-commerces'
    : isExternalOperatorOnlyUser
      ? '/business-external-operators'
      : null;

  routes.push(
    {
      path: '/',
      element: homeRedirect ? (
        <Navigate to={homeRedirect} replace />
      ) : (
        <Dashboard />
      ),
    },
    { path: RELEASE_NOTES_PATH, element: <ReleaseNotes /> },
    { path: '*', element: <ErrorPage code={404} /> }
  );

  if (permissions.includes(ROLES.FLEET.access)) {
    routes.push({
      path: '/fleet',
      element: <FleetLayout />,
      children: [...FleetRoutes],
    });
  } else {
    routes.push({
      path: '/fleet/*',
      element: <ErrorPage code={403} />,
    });
  }

  if (permissions.includes(ROLES.ATC.access)) {
    routes.push({
      path: '/atc',
      children: [...CustomerServiceRoutes],
    });
  } else {
    routes.push({
      path: '/atc/*',
      element: <ErrorPage code={403} />,
    });
  }

  if (permissions.includes(ROLES.ATC.access)) {
    routes.push({
      path: '/attend-track',
      children: [...AttendTrackRoutes],
    });
  } else {
    routes.push({
      path: '/attend-track/*',
      element: <ErrorPage code={403} />,
    });
  }

  if (permissions.includes(ROLES.ATC.access)) {
    routes.push({
      path: '/sila',
      element: <Sila />,
    });
  } else {
    routes.push({
      path: '/atc/*',
      element: <ErrorPage code={403} />,
    });
  }

  if (permissions.includes(ROLES.SCHEDULER.admin)) {
    routes.push({
      path: '/tasks',
      children: [...TasksRoutes],
    });
  } else {
    routes.push({
      path: '/tasks/*',
      element: <ErrorPage code={403} />,
    });
  }

  if (permissions.includes(ROLES.INVENTORY.access)) {
    routes.push({
      path: '/inventory',
      children: [...InventoryRoutes],
    });
  } else {
    routes.push({
      path: '/inventory/*',
      element: <ErrorPage code={403} />,
    });
  }

  if (userHasPermission(permissions, BUSINESS_ROLE_GROUPS.full)) {
    routes.push({
      path: '/business',
      element: <BusinessScopeGuard />,
      children: [...BusinessRoutes],
    });
  } else {
    routes.push({
      path: '/business/*',
      element: <ErrorPage code={403} />,
    });
  }

  // Los usuarios de comercio sólo entran acá: no tienen acceso al back office.
  if (
    userHasPermission(permissions, BUSINESS_ROLE_GROUPS.full) ||
    userHasPermission(permissions, COMMERCE_ROLE_NAMES as ROLES[])
  ) {
    routes.push({
      path: '/business-commerces',
      children: [...BusinessCommercesRoutes],
    });
  } else {
    routes.push({
      path: '/business-commerces/*',
      element: <ErrorPage code={403} />,
    });
  }

  // Los operadores externos sólo entran acá: no tienen acceso al back office.
  if (
    userHasPermission(permissions, BUSINESS_ROLE_GROUPS.full) ||
    userHasPermission(permissions, EXTERNAL_OPERATOR_ROLE_NAMES as ROLES[])
  ) {
    routes.push({
      path: '/business-external-operators',
      element: <BusinessExternalOperatorsScopeGuard />,
      children: [...BusinessExternalOperatorsRoutes],
    });
  } else {
    routes.push({
      path: '/business-external-operators/*',
      element: <ErrorPage code={403} />,
    });
  }

  if (permissions.includes(ROLES.BUSINESS.it)) {
    routes.push({
      path: TECH_CHECKS_PATH,
      children: [...TechChecksRoutes],
    });
  } else {
    routes.push({
      path: `${TECH_CHECKS_PATH}/*`,
      element: <ErrorPage code={403} />,
    });
  }

  if (permissions.includes(ROLES.BUSINESS.admin)) {
    routes.push({
      path: ADMINISTRATION_PATH,
      element: <Administration />,
    });
    routes.push({
      path: '/apps-versions',
      children: [...AppsVersionsRoutes],
    });
    routes.push({
      path: '/institutional-web',
      children: [...InstitutionalWebRoutes],
    });
  } else {
    routes.push({
      path: `${ADMINISTRATION_PATH}/*`,
      element: <ErrorPage code={403} />,
    });
    routes.push({
      path: '/apps-versions/*',
      element: <ErrorPage code={403} />,
    });
    routes.push({
      path: '/institutional-web/*',
      element: <ErrorPage code={403} />,
    });
  }
  return routes;
};

export default PrivateRoutesMiddleware;
